import { Link, useNavigate } from "react-router-dom";
import {
  BookOpen, BarChart3, Receipt, Users2, Briefcase, ArrowRight, Check,
  MessageSquare, ClipboardList, Lightbulb, Rocket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { services } from "@/config/site";
import { openWhatsApp } from "@/lib/whatsapp";
import { trackEvent, EVENTS } from "@/lib/analytics";

const SERVICE_ICONS = {
  bookkeeping: BookOpen,
  accounting: BarChart3,
  tax: Receipt,
  payroll: Users2,
  "business-support": Briefcase,
};

export function Eyebrow({ children, className = "" }) {
  return (
    <span className={`text-xs font-semibold uppercase tracking-[0.2em] text-[#2C5E43] ${className}`}>
      {children}
    </span>
  );
}

export function SectionHeading({ eyebrow, title, intro, center = false, className = "" }) {
  return (
    <div className={`${center ? "mx-auto max-w-2xl text-center" : "max-w-2xl"} ${className}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-[#18201B] sm:text-3xl lg:text-4xl">
        {title}
      </h2>
      {intro && <p className="mt-4 text-base leading-relaxed text-[#526057] sm:text-lg">{intro}</p>}
    </div>
  );
}

export function ConsultButton({ from = "section", className = "", children = "Request a Consultation", testId = "consult-btn" }) {
  const navigate = useNavigate();
  return (
    <Button
      data-testid={testId}
      onClick={() => { trackEvent(EVENTS.CONSULTATION_REQUEST, { from }); navigate("/assessment"); }}
      className={`bg-[#133827] text-white hover:bg-[#0d261a] ${className}`}
    >
      {children} <ArrowRight className="ml-1.5 h-4 w-4" />
    </Button>
  );
}

export function WhatsAppButton({ context = "general", className = "", testId = "whatsapp-btn" }) {
  return (
    <Button
      variant="outline"
      data-testid={testId}
      onClick={() => openWhatsApp(context)}
      className={`border-[#25D366] text-[#1a8a44] hover:bg-[#25D366]/10 ${className}`}
    >
      WhatsApp Us
    </Button>
  );
}

export function ValuePillars() {
  const pillars = [
    { title: "Clear Communication", body: "Professional financial information explained clearly — no unnecessary jargon." },
    { title: "Reliable Financial Administration", body: "Support with keeping your accounting records organised and current." },
    { title: "Practical Accounting Support", body: "Services aligned with what you or your business actually require." },
    { title: "Tax & Compliance Support", body: "Assistance with relevant accounting and tax responsibilities." },
    { title: "Financial Visibility", body: "Useful reporting to help you understand how your business is performing." },
  ];
  return (
    <section className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Why Bonsai"
          title="Financial administration should support your business, not distract from it."
          intro="Bonsai Financial provides practical accounting and financial support based on the requirements, size and complexity of each client."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {pillars.map((p, i) => (
            <div
              key={p.title}
              data-testid={`value-pillar-${i}`}
              className="rounded-2xl border border-slate-200/80 bg-[#F9F8F5] p-7 transition-shadow hover:shadow-md"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#133827]/8">
                <Check className="h-5 w-5 text-[#2C5E43]" />
              </div>
              <h3 className="mt-4 font-display text-lg font-semibold text-[#18201B]">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#526057]">{p.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function ServicesGrid({ withHeading = true, filterIds = null }) {
  const list = filterIds ? services.filter((s) => filterIds.includes(s.id)) : services;
  return (
    <section className="py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {withHeading && (
          <SectionHeading
            eyebrow="What We Do"
            title="Services tailored to your requirements."
            intro="Five core service areas, combined to match what your business actually needs."
          />
        )}
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {list.map((s) => {
            const Icon = SERVICE_ICONS[s.id] || Briefcase;
            return (
              <div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                className="group flex flex-col rounded-2xl border border-slate-200/80 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#133827] text-[#C68B59]">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#18201B]">{s.title}</h3>
                <p className="mt-2 text-sm text-[#526057]">{s.short}</p>
                <ul className="mt-4 space-y-2">
                  {s.items.slice(0, 5).map((it) => (
                    <li key={it} className="flex items-start gap-2 text-sm text-[#526057]">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#2C5E43]" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function CustomerJourneys() {
  const navigate = useNavigate();
  const paths = [
    { title: "Monthly Accounting & Bookkeeping", body: "For businesses requiring ongoing financial administration and accounting support.", cta: "Explore Monthly Support", to: "/services" },
    { title: "Tax & Financial Statements", body: "For individuals and businesses requiring tax, compliance and financial reporting assistance.", cta: "Explore Tax & Reporting", to: "/services" },
    { title: "Speak to Bonsai", body: "Not sure what you need? Start a short assessment and we'll point you in the right direction.", cta: "Request a Consultation", to: "/assessment" },
  ];
  return (
    <section className="bg-[#133827] py-16 text-white sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <Eyebrow className="text-[#C68B59]">Three Ways to Start</Eyebrow>
          <h2 className="mt-3 font-display text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
            Choose the path that fits where you are.
          </h2>
        </div>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {paths.map((p, i) => (
            <div key={p.title} data-testid={`journey-card-${i}`} className="flex flex-col rounded-2xl bg-white/5 p-7 ring-1 ring-white/10">
              <span className="font-display text-5xl font-bold text-[#C68B59]/40">0{i + 1}</span>
              <h3 className="mt-3 font-display text-xl font-semibold">{p.title}</h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-300">{p.body}</p>
              <Button
                onClick={() => navigate(p.to)}
                data-testid={`journey-cta-${i}`}
                className="mt-6 w-full bg-[#C68B59] text-white hover:bg-[#b2794a]"
              >
                {p.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HowItWorks() {
  const steps = [
    { icon: ClipboardList, title: "Tell us about your business", body: "Complete a short assessment so Bonsai can understand who you are and what support you require." },
    { icon: MessageSquare, title: "Speak to Bonsai", body: "Your information is reviewed and a consultation can be arranged." },
    { icon: Lightbulb, title: "Receive a recommended solution", body: "Bonsai determines the appropriate services based on your circumstances and requirements." },
    { icon: Rocket, title: "Focus on your business", body: "Once engaged, Bonsai handles the agreed accounting and financial administration while you focus on operating your business." },
  ];
  return (
    <section className="py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionHeading eyebrow="How Bonsai Works" title="A simple, four-step process." center />
        <div className="mt-14 grid gap-8 md:grid-cols-4">
          {steps.map((s, i) => (
            <div key={s.title} data-testid={`how-step-${i}`} className="relative text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#133827] text-[#C68B59]">
                <s.icon className="h-6 w-6" />
              </div>
              <span className="mt-4 block text-xs font-semibold uppercase tracking-widest text-[#C68B59]">Step {i + 1}</span>
              <h3 className="mt-1 font-display text-lg font-semibold text-[#18201B]">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#526057]">{s.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <ConsultButton from="how-it-works" testId="how-start-assessment-btn">Start Your Assessment</ConsultButton>
        </div>
      </div>
    </section>
  );
}

export function FaqAccordion({ items }) {
  return (
    <Accordion type="single" collapsible className="mt-8 w-full">
      {items.map((f, i) => (
        <AccordionItem key={i} value={`item-${i}`} data-testid={`faq-item-${i}`}>
          <AccordionTrigger className="text-left font-display text-base font-semibold text-[#18201B] hover:text-[#2C5E43]">
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="text-sm leading-relaxed text-[#526057]">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function CtaBand({ title = "Ready to get your finances organised?", from = "cta-band" }) {
  return (
    <section className="bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-3xl bg-[#133827] px-8 py-12 text-center sm:px-14 sm:py-16">
          <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-[#C68B59]/15 blur-2xl" />
          <h2 className="relative font-display text-2xl font-bold text-white sm:text-3xl">{title}</h2>
          <p className="relative mx-auto mt-3 max-w-xl text-sm text-slate-300 sm:text-base">
            Start with a short assessment. There's no obligation — submitting the form does not by
            itself create a professional engagement.
          </p>
          <div className="relative mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <ConsultButton from={from} testId="ctaband-consult-btn" className="w-full sm:w-auto" />
            <WhatsAppButton context="consultation" testId="ctaband-whatsapp-btn" className="w-full bg-white/5 sm:w-auto" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function PageHeader({ eyebrow, title, intro }) {
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20 lg:px-8">
        <div className="animate-fade-up max-w-3xl">
          <Eyebrow>{eyebrow}</Eyebrow>
          <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-[#18201B] sm:text-5xl">
            {title}
          </h1>
          {intro && <p className="mt-5 text-lg leading-relaxed text-[#526057]">{intro}</p>}
        </div>
      </div>
    </section>
  );
}
